import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { flatDictionaries } from '../i18n/dictionaries';

const I18nContext = createContext();

/**
 * Map i18n language code to Intl-compatible locale code
 * @param {string} lang - Language code ('ru', 'en', 'ky')
 * @returns {string} Intl locale code ('ru-RU', 'en-US', 'ky-KG')
 */
export const getLocaleCode = (lang) => {
  const map = { ru: 'ru-RU', en: 'en-US', ky: 'ky-KG' };
  return map[lang] || 'ru-RU';
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    return localStorage.getItem('admin_locale') || 'ru';
  });

  useEffect(() => {
    localStorage.setItem('admin_locale', locale);
  }, [locale]);

  const t = useCallback((key, fallback) => {
    return flatDictionaries[locale]?.[key] || fallback || key;
  }, [locale]);

  const changeLocale = useCallback((newLocale) => {
    if (['ru', 'en', 'ky'].includes(newLocale)) {
      setLocale(newLocale);
    }
  }, []);

  const localeCode = useMemo(() => getLocaleCode(locale), [locale]);

  const value = useMemo(() => ({ locale, localeCode, t, changeLocale }), [locale, localeCode, t, changeLocale]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};
